'use client';

import Script from 'next/script';
import { useEffect } from 'react';
import { waitForOT } from '../onetrust';
import { buildConsentSnapshot } from '../consent-snapshot';
import type { ConsentSnapshot } from '../models/consent';

const OT_SDK_STUB_SRC = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';

export interface OneTrustScriptsProps {
  /** OneTrust's `data-domain-script` id — differs per brand/environment. */
  websiteId?: string;
  /** Called once OneTrust is ready, and again whenever consent changes. */
  onConsentChanged?: (snapshot: ConsentSnapshot) => void;
}

/**
 * OneTrust cookie consent scripts:
 * - `otSDKStub.js` must load with `beforeInteractive` so cookies/scripts are
 *   blocked before hydration.
 * - `window.OptanonWrapper` is OneTrust's callback hook, invoked whenever it
 *   initializes or consent changes.
 */
export function OneTrustScripts({ websiteId, onConsentChanged }: OneTrustScriptsProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.OptanonWrapper = () => {
      const snapshot = buildConsentSnapshot();
      onConsentChanged?.(snapshot);
    };
    void waitForOT().then(() => {
      const snapshot = buildConsentSnapshot();
      onConsentChanged?.(snapshot);
    });
  }, [onConsentChanged, websiteId]);

  return (
    // eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
    <Script
      src={OT_SDK_STUB_SRC}
      data-domain-script={websiteId}
      type='text/javascript'
      strategy='beforeInteractive'
    />
  );
}
